import { Attributes } from '@leverege/ui-attributes';
import { Plugins, Match, View } from '@leverege/plugin';
import { Dialogs, Toast } from '@leverege/ui-elements';
import StringUtil from '@leverege/string-util';
import I18N from '@leverege/i18n';
const {
  t
} = I18N.ns('molten.imagine.view.utils');
function getResultRenderer(type, matchContext, blueprint) {
  const view = Plugins.createView(type, {
    name: blueprint.type,
    filter: plugin => {
      return Match.isMatch(matchContext, plugin.matches);
    }
  });
  const sorted = view.get().sort(View.defaultSort);
  return sorted[0];
}
const handleFormResult = (result, opts) => {
  const {
    delegate,
    blueprint,
    relationship,
    initialData = {},
    onClose: outerOnClose,
    type = 'create',
    target
  } = opts;
  const matchContext = {
    objectType: blueprint.type,
    path: relationship.path
  };
  const successRenderer = getResultRenderer('BlueprintFormSuccess', matchContext, blueprint);
  if (successRenderer) {
    const Component = successRenderer.component;
    const onClose = Dialogs.show({
      component: Component,
      props: {
        type,
        delegate,
        blueprint,
        relationship,
        title: t('successtitle', {
          type: StringUtil.capitalize(type),
          blueprintName: blueprint.name
        }),
        titleIcon: blueprint.getIcon(`${type}DialogIcon`) || blueprint.getIcon('screenIcon'),
        result,
        target,
        initialData,
        onOkay: () => {
          onClose();
          outerOnClose();
        }
      }
    });
  } else {
    Toast.success(t('successToast', {
      blueprintName: blueprint.name,
      type
    }));
  }
};
const handleFormError = (error, opts) => {
  const {
    delegate,
    blueprint,
    relationship,
    initialData = {},
    onClose: outerOnClose,
    type = 'create',
    target
  } = opts;
  const matchContext = {
    objectType: blueprint.type,
    path: relationship.path
  };
  const errorRenderer = getResultRenderer('BlueprintFormError', matchContext, blueprint);
  if (errorRenderer) {
    const Component = errorRenderer.component;
    const onClose = Dialogs.show({
      component: Component,
      props: {
        type,
        delegate,
        blueprint,
        relationship,
        title: t('errorTitle', {
          blueprintName: blueprint.name,
          type: StringUtil.capitalize(type)
        }),
        titleIcon: blueprint.getIcon(`${type}DialogIcon`) || blueprint.getIcon('screenIcon'),
        error,
        target,
        initialData,
        onRetry: extra => {
          onClose();
        },
        onCancel: () => {
          onClose();
          outerOnClose();
        }
      }
    });
  } else {
    Toast.error([t('errorToast', {
      type,
      blueprintName: blueprint.name
    }), error.message]);
  }
};

/**
 * Returns either the blueprint name or namePlural depending on the
 * number of items in targets
 */
const blueprintName = (blueprint, targets) => {
  return targets.length > 1 ? blueprint.namePlural : blueprint.name;
};
const getMsgTargets = (blueprint, targets) => {
  const num = targets.length;
  if (num === 0) {
    return null;
  }
  if (num === 1) {
    const name = getName(targets[0]);
    return name || blueprint.name;
  }
  if (num === 2) {
    const name1 = getName(targets[0]);
    const name2 = getName(targets[1]);
    return name1 && name2 ? t('2Names', {
      name1,
      name2
    }) : t('numberOfType', {
      count: num,
      blueprintName: blueprint.namePlural
    });
  }
  if (num === 3) {
    const name1 = getName(targets[0]);
    const name2 = getName(targets[1]);
    const name3 = getName(targets[2]);
    return name1 && name2 && name3 ? t('3Names', {
      name1,
      name2,
      name3
    }) : t('numberOfType', {
      count: num,
      blueprintName: blueprint.namePlural
    });
  }
  return t('numberOfType', {
    count: num,
    blueprintName: blueprint.namePlural
  });
};
function getName(tgt) {
  return Attributes.get('name', tgt.type, tgt.data);
}
function attributeSort(a, b) {
  const as = a.source?.attribute?.getTagStartingWith?.('formSort:', true) || 'zzz';
  const bs = b.source?.attribute?.getTagStartingWith?.('formSort:', true) || 'zzz';
  return as.localeCompare(bs);
}
function getAttributes(type, action = 'create', dedupeUpdatePath = true) {
  const attrs = Attributes.getAttributesFor(type).sort(attributeSort);
  if (!dedupeUpdatePath) {
    return attrs;
  }
  const updatePaths = {};
  return attrs.filter(attr => {
    if (attr.source?.attribute?.hasTag?.(`${action}:exclude`)) {
      return false;
    }
    const updatePath = attr.source?.updatePath;
    if (!updatePath) {
      return true;
    }
    if (updatePath && updatePaths[updatePath]) {
      return false;
    }
    updatePaths[updatePath] = true;
    return true;
  });
}
export default {
  handleFormResult,
  handleFormError,
  blueprintName,
  getMsgTargets,
  getAttributes
};